import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { ILearningCenterSearchBarFields } from '@/types/contentful';

import { FilterButton } from './FilterButton';
import { FilterSelect } from './FilterSelect';
import { SearchFilters, DEFAULT_FILTERS } from './shared';

interface FiltersProps {
  filters: ILearningCenterSearchBarFields;
  open: boolean;
  onChange: (filters: SearchFilters) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

interface FilterRefs {
  topics: MutableRefObject<MultiSelectRef | undefined>;
  mediaTypes: MutableRefObject<MultiSelectRef | undefined>;
  difficulties: MutableRefObject<MultiSelectRef | undefined>;
  sortBy: MutableRefObject<MultiSelectRef | undefined>;
}

interface MultiSelectRef {
  clearValue: () => void;
  updateValues: (value: string[]) => void;
}

export function Filters({
  filters,
  open,
  onChange,
  onSubmit,
  onCancel
}: FiltersProps) {
  const router = useRouter();
  const [selectedFilters, setSelectedFilters] =
    useState<SearchFilters>(DEFAULT_FILTERS);
  const [overflow, setOverflow] = useState('hidden');
  const filterRefs: FilterRefs = {
    topics: useRef<MultiSelectRef>(),
    mediaTypes: useRef<MultiSelectRef>(),
    difficulties: useRef<MultiSelectRef>(),
    sortBy: useRef<MultiSelectRef>()
  };

  useEffect(() => {
    setOverflow('hidden');
    if (open) {
      setTimeout(() => setOverflow('visible'), 500);
    }
  }, [open]);

  useEffect(() => {
    // copy the current filters
    const newSelectedFilters = {
      ...selectedFilters
    };

    // check which filters are in the url and update the selected filters object
    // Also updates the filter refs
    Object.keys(selectedFilters).forEach((filterName) => {
      if (router?.query?.[filterName]) {
        const filterValue =
          router?.query?.[filterName]?.toString()?.split(',') ?? [];
        newSelectedFilters[filterName] = filterValue;
        filterRefs[filterName as keyof FilterRefs]?.current?.updateValues(
          filterValue
        );
      }
    });

    // finally update the selected filters object
    setSelectedFilters(newSelectedFilters);
  }, [
    router?.query?.topics,
    router?.query?.mediaTypes,
    router?.query?.difficulties,
    router?.query?.sortBy
  ]);

  const onSelectFilter = (name: string, value: string[]) => {
    const filterName = name as keyof SearchFilters;
    const newSelectedFilters = {
      ...selectedFilters,
      [filterName]: value
    };

    setSelectedFilters(newSelectedFilters);
    filterRefs[name as keyof FilterRefs]?.current?.updateValues(value);
    onChange(newSelectedFilters);
  };

  const handleCancel = () => {
    Object.values(filterRefs).forEach(
      (filterRef) => filterRef.current?.updateValues([])
    );
    setSelectedFilters({ ...DEFAULT_FILTERS });
    onCancel();
  };

  return (
    <div
      data-testid="search-filters"
      className={`h-auto overflow-${overflow} transition-all ease-out ${
        open
          ? 'max-h-[350px] opacity-100 duration-500 tablet:max-h-[130px]'
          : '!max-h-0 opacity-0 duration-300'
      }`}
    >
      <section className="-m-2 mt-4 box-border flex w-full flex-[1_0_100%] flex-wrap justify-center [&>*]:p-2">
        <div className="box-column flex w-full flex-col tablet:w-3/12">
          <FilterSelect
            ref={filterRefs.topics}
            label="Topic"
            options={filters.topic}
            value={selectedFilters.topics}
            onSelect={(value) => onSelectFilter('topics', value)}
          />
        </div>
        <div className="box-column flex w-full flex-col tablet:w-3/12">
          <FilterSelect
            ref={filterRefs.mediaTypes}
            label="Media type"
            options={filters.mediaType}
            value={selectedFilters.mediaTypes}
            onSelect={(value) => onSelectFilter('mediaTypes', value)}
          />
        </div>
        <div className="box-column flex w-full flex-col tablet:w-3/12">
          <FilterSelect
            ref={filterRefs.difficulties}
            label="Difficulty"
            options={filters.difficulty}
            value={selectedFilters.difficulties}
            onSelect={(value) => onSelectFilter('difficulties', value)}
          />
        </div>
        <div className="box-column flex w-full flex-col tablet:w-3/12">
          <FilterSelect
            ref={filterRefs.sortBy}
            label="Sort by"
            options={filters.sortBy}
            value={selectedFilters.sortBy}
            onSelect={(value) => onSelectFilter('sortBy', value)}
          />
        </div>
        <footer className="-m-2 box-border flex w-full min-w-full flex-wrap justify-center [&>*]:p-2">
          <div className="box-column w-6/12">
            <FilterButton
              type="reset"
              onClick={handleCancel}
              color="bg-[#F3F3F3]"
              activeColor="bg-[#E9E9E9]"
            >
              Cancel
            </FilterButton>
          </div>
          <div className="box-column w-6/12">
            <FilterButton
              type="submit"
              onClick={onSubmit}
              color="bg-teal"
              activeColor="bg-[#1dbca2]"
            >
              Search
            </FilterButton>
          </div>
        </footer>
      </section>
    </div>
  );
}
